import { useEffect, useState } from "react";
import { useProjectFile, useSaveFile } from "../../api/hooks";
import type { Catalog, OutputDef } from "../../api/types";
import { useSession } from "../../app/store";
import { Split } from "../../components/Split";
import { errText } from "../../components/ui";
import { CodeEditor } from "../../editor/CodeEditor";
import { MEDIA_TYPES, emptyOutput, starterTemplate } from "./model";

export function OutputsEditor({
  outputs,
  onChange,
  catalog,
  projectId,
  selectedIndex,
  onSelect,
  templateDraft,
  onTemplateDraft,
}: {
  outputs: OutputDef[];
  onChange: (o: OutputDef[]) => void;
  catalog: Catalog | undefined;
  projectId: string;
  selectedIndex: number;
  onSelect: (i: number) => void;
  templateDraft: string | null;
  onTemplateDraft: (text: string | null) => void;
}) {
  const out = outputs[selectedIndex];
  const update = (patch: Partial<OutputDef>) => onChange(outputs.map((o, i) => (i === selectedIndex ? { ...o, ...patch } : o)));

  return (
    <Split id="report-outputs" sizes={[200, "1fr"]} minSizes={[120, 280]}>
      <div className="pane">
        <div className="pane-head">
          Outputs
          <span className="spacer" />
          <button className="btn ghost sm" onClick={() => (onChange([...outputs, emptyOutput(`output_${outputs.length + 1}`)]), onSelect(outputs.length))}>
            ＋
          </button>
        </div>
        <div className="list">
          {outputs.map((o, i) => (
            <div key={i} className={`list-item${i === selectedIndex ? " sel" : ""}`} onClick={() => onSelect(i)}>
              <span className="name mono">{o.name}</span>
              <span className="badge">{o.renderer === "legacy_bracket" ? o.format ?? "bracket" : o.renderer}</span>
              {!o.enabled ? <span className="badge warn">off</span> : null}
            </div>
          ))}
        </div>
        <div className="pane-body small dim">
          A report may write several artifacts per snapshot: e.g. a SAGE file and a CSV or JSON alongside it.
        </div>
      </div>
      <div className="pane">
        {out ? (
          <OutputForm
            key={selectedIndex}
            out={out}
            update={update}
            catalog={catalog}
            projectId={projectId}
            onRemove={() => {
              onChange(outputs.filter((_, i) => i !== selectedIndex));
              onSelect(Math.max(0, selectedIndex - 1));
            }}
            templateDraft={templateDraft}
            onTemplateDraft={onTemplateDraft}
          />
        ) : (
          <div className="empty">Add an output.</div>
        )}
      </div>
    </Split>
  );
}

function OutputForm({
  out,
  update,
  catalog,
  projectId,
  onRemove,
  templateDraft,
  onTemplateDraft,
}: {
  out: OutputDef;
  update: (p: Partial<OutputDef>) => void;
  catalog: Catalog | undefined;
  projectId: string;
  onRemove: () => void;
  templateDraft: string | null;
  onTemplateDraft: (t: string | null) => void;
}) {
  const toast = useSession((s) => s.toast);
  const templateFile = useProjectFile(projectId, out.template ?? null);
  const saveFile = useSaveFile(projectId);
  const [mode, setMode] = useState<"file" | "inline">(out.template ? "file" : "inline");
  useEffect(() => setMode(out.template ? "file" : "inline"), [out.template]);

  const fileText = templateDraft ?? templateFile.data?.content ?? "";
  const fileDirty = templateDraft !== null && templateDraft !== (templateFile.data?.content ?? "");
  const saveTemplate = () => {
    if (!out.template || templateDraft === null) return;
    saveFile.mutate(
      { path: out.template, content: templateDraft },
      { onSuccess: () => (toast("Template saved", "success"), onTemplateDraft(null)), onError: (e) => toast(errText(e), "error") },
    );
  };

  const newTemplateFile = () => {
    const ext = out.renderer === "jinja2" ? "j2" : "txt";
    const name = prompt("Template file name (in templates/)", `${out.name}.${ext}`);
    if (!name) return;
    const path = `templates/${name}`;
    saveFile.mutate(
      { path, content: out.template_text ?? "" },
      { onSuccess: () => update({ template: path, template_text: null }), onError: (e) => toast(errText(e), "error") },
    );
  };

  return (
    <div className="col" style={{ padding: 10, minHeight: 0, flex: 1 }}>
      <div className="row wrap">
        <label className="field">
          <span>Name</span>
          <input className="inp sm" value={out.name} onChange={(e) => update({ name: e.target.value })} />
        </label>
        <label className="field">
          <span>Renderer</span>
          <select
            className="inp sm"
            value={out.renderer}
            onChange={(e) => {
              const renderer = e.target.value as OutputDef["renderer"];
              const format = renderer === "legacy_bracket" ? out.format ?? "TXT" : null;
              const blank = !out.template && !out.template_text?.trim();
              update({ renderer, format, ...(blank ? { template_text: starterTemplate(renderer, format) } : {}) });
            }}
          >
            <option value="jinja2">Jinja2 template</option>
            <option value="legacy_bracket">Bracket template (SAGE / DIG / TXT)</option>
            <option value="python">Python render() function</option>
          </select>
        </label>
        {out.renderer === "legacy_bracket" ? (
          <>
            <label className="field">
              <span>Format</span>
              <select className="inp sm" value={out.format ?? "TXT"} onChange={(e) => update({ format: e.target.value as OutputDef["format"] })}>
                {(catalog?.legacy_formats ?? ["SAGE_FORMAT", "DIG", "TXT"]).map((f) => (
                  <option key={f}>{f}</option>
                ))}
              </select>
            </label>
            <label className="field">
              <span>[Phase] token</span>
              <input className="inp sm" value={out.phase ?? ""} onChange={(e) => update({ phase: e.target.value })} placeholder="TAKEOFF" />
            </label>
          </>
        ) : null}
        <label className="field">
          <span>[Report_Type] token</span>
          <input className="inp sm" value={out.report_type ?? ""} onChange={(e) => update({ report_type: e.target.value })} placeholder={"defaults to report name"} />
        </label>
        <label className="field">
          <span>Media type</span>
          <select className="inp sm" value={out.media_type} onChange={(e) => update({ media_type: e.target.value })}>
            {[...new Set([...MEDIA_TYPES, out.media_type])].map((m) => (
              <option key={m}>{m}</option>
            ))}
          </select>
        </label>
        <label className="field">
          <span>Line endings</span>
          <select className="inp sm" value={out.newline ?? ""} onChange={(e) => update({ newline: e.target.value || null })}>
            <option value="">keep template's</option>
            <option value={"\n"}>LF</option>
            <option value={"\r\n"}>CRLF</option>
          </select>
        </label>
        <label className="field row" style={{ flexDirection: "row", alignItems: "center", marginTop: 14 }}>
          <input type="checkbox" checked={out.enabled} onChange={(e) => update({ enabled: e.target.checked })} /> enabled
        </label>
        <span className="spacer" />
        <button className="btn sm danger" onClick={onRemove}>
          Remove output
        </button>
      </div>
      <label className="field">
        <span>
          Filename pattern — tokens:{" "}
          {(catalog?.filename_tokens ?? []).map((t) => (
            <button key={t} className="btn ghost sm" style={{ padding: "0 4px" }} onClick={() => update({ filename: out.filename + `[${t}]` })}>
              [{t}]
            </button>
          ))}
          <span className="faint"> · also [flight.&lt;field&gt;], [values.&lt;key&gt;] or a {"{{ jinja }}"} expression</span>
        </span>
        <input className="inp sm mono" value={out.filename} onChange={(e) => update({ filename: e.target.value })} />
      </label>

      {out.renderer === "python" ? (
        <label className="field">
          <span>Function (renderers/file.py:render) — must return str or bytes</span>
          <div className="row">
            <input className="inp sm mono" style={{ flex: 1 }} value={out.function ?? ""} onChange={(e) => update({ function: e.target.value })} placeholder="renderers/my_report.py:render" list="renderer-fns" />
            <datalist id="renderer-fns">
              {catalog?.renderers.map((r) => (
                <option key={r.function} value={r.function} />
              ))}
            </datalist>
          </div>
          <span className="faint small">
            Signature: <code>def render(snapshot, ctx) -&gt; str | bytes</code>; <code>ctx</code> is the same dict a Jinja2 template sees.
            Create the file from the Templates &amp; renderers list on the left.
          </span>
        </label>
      ) : (
        <>
          <div className="row">
            <span className="small dim">Template</span>
            <div className="mode-pill">
              <button className={mode === "file" ? "on" : ""} onClick={() => setMode("file")}>
                project file
              </button>
              <button className={mode === "inline" ? "on" : ""} onClick={() => (setMode("inline"), update({ template: null }))}>
                inline
              </button>
            </div>
            {mode === "inline" && !out.template_text?.trim() ? (
              <>
                <span className="badge warn">no template – the output cannot render</span>
                <button className="btn sm" onClick={() => update({ template_text: starterTemplate(out.renderer, out.format) ?? "" })}>
                  Insert starter
                </button>
              </>
            ) : null}
            {mode === "file" ? (
              <>
                <select className="inp sm" value={out.template ?? ""} onChange={(e) => update({ template: e.target.value || null })}>
                  <option value="">— choose a template file —</option>
                  {catalog?.templates.map((t) => (
                    <option key={t.path} value={t.path}>
                      {t.path}
                    </option>
                  ))}
                </select>
                <button className="btn sm" onClick={newTemplateFile}>
                  New file…
                </button>
                {out.template ? (
                  <button className="btn sm primary" disabled={!fileDirty || saveFile.isPending} onClick={saveTemplate}>
                    Save template
                  </button>
                ) : null}
                {fileDirty ? <span className="badge warn">template modified</span> : null}
              </>
            ) : null}
          </div>
          <div style={{ flex: 1, minHeight: 260, border: "1px solid var(--rs-border)", borderRadius: 6, overflow: "hidden" }}>
            {mode === "file" ? (
              out.template ? (
                templateFile.data ? (
                  <CodeEditor
                    path={`${projectId}/${out.template}`}
                    value={fileText}
                    onChange={onTemplateDraft}
                    onSave={saveTemplate}
                    language={out.renderer === "legacy_bracket" || out.renderer === "jinja2" ? "jinja" : undefined}
                    wordWrap={false}
                  />
                ) : (
                  <div className="empty small">{templateFile.isError ? errText(templateFile.error) : "loading…"}</div>
                )
              ) : (
                <div className="empty small">Choose or create a template file.</div>
              )
            ) : (
              <CodeEditor path={`${projectId}/inline/${out.name}.j2`} value={out.template_text ?? ""} onChange={(v) => update({ template_text: v })} language="jinja" />
            )}
          </div>
        </>
      )}
    </div>
  );
}
